import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ExternalLink, Sparkles, CheckCircle2, Gift, Star, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const betaBenefits = [
  {
    icon: CheckCircle2,
    title: "No Obligations",
    description: "No commercial obligations and no minimum usage requirements — participate entirely at your own pace.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: Gift,
    title: "Exclusive Discounts",
    description: "All beta participants receive discount offers. Most active participants get 50%+ discounts and exclusive perks on full release.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
  },
  {
    icon: Star,
    title: "Special Recognition",
    description: "Highly active participants may be featured on our website and LinkedIn, and could receive invitations to become medical advisors.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
  {
    icon: Users,
    title: "Early Access Forever",
    description: "Active beta users will be invited to all future beta programs before public release — a permanent inside track.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
];

const programDetails = [
  "No commercial obligations to use any beta features",
  "No minimum usage requirements during the beta",
  "All beta participants will receive discount offers, including those who are less or not active",
  "Most active beta participants will receive discounts (50% or more) and other exclusive offers on full product release",
  "Highly active participants may be featured on our website and LinkedIn, and could receive invitations to become medical advisors",
  "Active users will be invited to future beta programs before public release",
];

const BetaProgram = () => {
  const [form, setForm] = useState({
    name: "",
    type: "doctor",
    email: "",
    phone: "",
    bio: "",
    linkedin: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      alert("Please provide your name and email.");
      return;
    }

    setLoading(true);
    try {
      const apiKey = import.meta.env.VITE_API_KEY;
      const endpoint = "https://n9ohxdwyw9.execute-api.ap-south-1.amazonaws.com/prod/betaSubmit";

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify(form),
      });

      const data = await resp.text();
      console.log("Lambda response:", resp.status, data);

      if (!resp.ok) {
        throw new Error(`Submission failed (${resp.status})`);
      }

      setSubmitted(true);
    } catch (err: unknown) {
      console.error(err);
      alert("There was an error submitting the form. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Exclusive Beta Program
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            <span className="text-gradient-primary">Beta Program</span>
            <br />
            <span className="text-foreground">for Clinicians</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Shape the future of clinical technology. Join our exclusive beta opening on{" "}
            <strong className="text-foreground">2nd May 2026</strong> — designed for doctors,
            medical assistants, and clinics.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur border border-border/60 shadow-sm text-sm font-medium text-foreground">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Beta opens 2nd May 2026
            </div>
            <a
              href="https://www.linkedin.com/company/wellnextek"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur border border-border/60 shadow-sm text-sm font-medium text-primary hover:bg-primary/5 transition"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Follow us on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* ─── Benefits Cards ───────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Why participate?
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">What's in it for you</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Real benefits for real participation — no strings attached.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {betaBenefits.map(({ icon: Icon, title, description, accent, bg, bar }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${bar}`} />
                <div className="p-6">
                  <div className={`h-11 w-11 rounded-2xl ${bg} flex items-center justify-center mb-4`}>
                    <Icon className={`h-5 w-5 ${accent}`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Program Details + Registration Form ─────────────────────────── */}
      <section className="py-12 pb-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-[1fr_480px] gap-10 items-start">

            {/* Program Details */}
            <div>
              <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
                Program Details
              </span>
              <h2 className="text-3xl font-bold text-foreground mb-4">What to expect</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                This program is designed to gather real-world feedback, validate clinical
                workflows, and improve product usability prior to public release. All participant
                data remains private and will not be shared publicly; anonymized data may be used
                for validation and research in accordance with our privacy policy.
              </p>
              <ul className="space-y-3">
                {programDetails.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Registration Form */}
            <div className="rounded-3xl border border-border/60 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.08)] overflow-hidden">
              <div className="h-[3px] w-full bg-gradient-to-r from-primary via-secondary to-accent" />
              <div className="p-8">
                {!submitted ? (
                  <>
                    <h3 className="text-xl font-bold text-foreground mb-1">Register for Beta</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Fill in your details and we'll reach out with next steps.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Name <span className="text-primary">*</span>
                        </label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Type <span className="text-primary">*</span>
                        </label>
                        <select
                          name="type"
                          value={form.type}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        >
                          <option value="doctor">Doctor</option>
                          <option value="medical-assistant">Medical Assistant</option>
                          <option value="clinic">Clinic</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Email <span className="text-primary">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Phone{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Short Bio{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <textarea
                          name="bio"
                          value={form.bio}
                          onChange={handleChange}
                          rows={3}
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          LinkedIn{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <input
                          name="linkedin"
                          value={form.linkedin}
                          onChange={handleChange}
                          placeholder="https://www.linkedin.com/in/your-profile"
                          className="w-full rounded-xl border border-input px-4 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>

                      <div className="pt-2">
                        <Button
                          type="submit"
                          size="lg"
                          disabled={loading}
                          className="w-full rounded-xl gap-2 shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
                        >
                          {loading ? "Submitting…" : "Register for Beta"}
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 className="h-7 w-7 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Thank you for your interest!
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      We received your submission and will contact you with next steps.
                    </p>
                    <Button onClick={() => navigate("/")} className="rounded-xl">
                      Back to Home
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BetaProgram;
